import { supabase } from './supabase';

/**
 * Dashboard alert engine — queries the live data and returns the cards
 * that should fire for the active site / org. Quiet when calm.
 *
 * Each alert has a severity, a title, a one-line body and an optional
 * action link. Dashboard renders them in severity order (red, then amber).
 */

export type AlertSeverity = 'red' | 'amber';

export interface DashboardAlert {
  id: string;                // stable id for keys
  severity: AlertSeverity;
  title: string;
  body: string;
  href?: string;             // tap target
  actionLabel?: string;      // "Resolve", "Close out", etc.
}

const DAY_MS = 86400000;

/** Get all alerts for the active site + org. */
export async function getDashboardAlerts(
  orgId: string,
  siteId: string | null,
): Promise<DashboardAlert[]> {
  const alerts: DashboardAlert[] = [];

  if (!siteId) {
    // No active site — no site-specific alerts. Could still surface org-wide.
    const orgAlerts = await getOrgWideAlerts(orgId);
    return orgAlerts;
  }

  const [
    issueAlerts,
    permitAlerts,
    dailyCheckAlert,
    weeklyCheckAlert,
    expiryAlerts,
    wrapUpAlert,
    firstAiderAlert,
  ] = await Promise.all([
    getOpenIssueAlerts(siteId),
    getPermitAlerts(orgId, siteId),
    getDailyCheckAlert(siteId),
    getWeeklyCheckAlert(siteId),
    getOrgWideAlerts(orgId),
    getWrapUpNudge(siteId),
    getFirstAiderAlert(siteId),
  ]);

  alerts.push(...issueAlerts);
  alerts.push(...permitAlerts);
  if (dailyCheckAlert) alerts.push(dailyCheckAlert);
  if (weeklyCheckAlert) alerts.push(weeklyCheckAlert);
  alerts.push(...expiryAlerts);
  if (wrapUpAlert) alerts.push(wrapUpAlert);
  if (firstAiderAlert) alerts.push(firstAiderAlert);

  // Severity order: red first
  alerts.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'red' ? -1 : 1));
  return alerts;
}

// ============================================================
// #1 Open issues — tick list items with state='fail' >3 days old
// ============================================================

async function getOpenIssueAlerts(siteId: string): Promise<DashboardAlert[]> {
  // Read tick list runs from last 90 days and find unresolved issues.
  const since = new Date(Date.now() - 90 * DAY_MS).toISOString();
  const { data, error } = await supabase
    .from('tick_list_runs')
    .select('id, kind, responses, created_at')
    .eq('site_id', siteId)
    .gte('created_at', since)
    .order('created_at', { ascending: false });
  if (error) return [];

  // Find oldest run that contains an UNRESOLVED issue
  let oldestIssueAt: Date | null = null;
  let issueCount = 0;
  for (const run of data ?? []) {
    const responses = (run.responses ?? {}) as Record<string, { state?: string; resolved?: boolean }>;
    const issues = Object.values(responses).filter((r) => r?.state === 'fail' && !r?.resolved);
    if (issues.length > 0) {
      issueCount += issues.length;
      const at = new Date(run.created_at);
      if (!oldestIssueAt || at < oldestIssueAt) oldestIssueAt = at;
    }
  }

  if (oldestIssueAt) {
    const ageDays = Math.floor((Date.now() - oldestIssueAt.getTime()) / DAY_MS);
    if (ageDays >= 3) {
      return [{
        id: 'issue-old',
        severity: 'red',
        title: `Issue still open from ${ageDays} day${ageDays === 1 ? '' : 's'} ago`,
        body: `${issueCount} unresolved issue${issueCount === 1 ? '' : 's'} from recent tick lists. Check and close out.`,
        actionLabel: 'View issues',
        href: `/sites/${siteId}/issues`,
      }];
    }
    if (issueCount > 0) {
      return [{
        id: 'issue-recent',
        severity: 'amber',
        title: `${issueCount} open issue${issueCount === 1 ? '' : 's'}`,
        body: 'Recent tick list issues still need closing out.',
        actionLabel: 'View issues',
        href: `/sites/${siteId}/issues`,
      }];
    }
  }

  return [];
}

// ============================================================
// #2, #7 Permits — expired without close-out, or expiring soon
// ============================================================

async function getPermitAlerts(_orgId: string, siteId: string): Promise<DashboardAlert[]> {
  const out: DashboardAlert[] = [];
  const { data, error } = await supabase
    .from('permits')
    .select('id, title, expires_at, status')
    .eq('site_id', siteId)
    .in('status', ['open', 'active']);
  if (error) return [];

  const now = Date.now();
  let expiredCount = 0;
  let expiringCount = 0;
  for (const p of data ?? []) {
    const expiresAt = new Date(p.expires_at).getTime();
    if (expiresAt < now) {
      expiredCount += 1;
    } else if (expiresAt - now < 30 * 60 * 1000) {
      expiringCount += 1;
    }
  }

  if (expiredCount > 0) {
    out.push({
      id: 'permit-expired',
      severity: 'red',
      title: `${expiredCount} permit${expiredCount === 1 ? '' : 's'} expired without close-out`,
      body: 'A permit ran out and was never closed. Open it and close it now.',
      actionLabel: 'Close out',
      href: `/sites/${siteId}/permits`,
    });
  }
  if (expiringCount > 0) {
    out.push({
      id: 'permit-expiring',
      severity: 'amber',
      title: `${expiringCount} permit${expiringCount === 1 ? '' : 's'} expires in next 30 min`,
      body: 'Plan close-out (and any fire watch period) now.',
      actionLabel: 'Open permits',
      href: `/sites/${siteId}/permits`,
    });
  }

  return out;
}

// ============================================================
// #6 Daily check not done today (after 09:00)
// ============================================================

async function getDailyCheckAlert(siteId: string): Promise<DashboardAlert | null> {
  const now = new Date();
  if (now.getHours() < 9) return null;
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from('tick_list_runs')
    .select('id')
    .eq('site_id', siteId)
    .eq('kind', 'daily')
    .gte('created_at', startOfDay.toISOString())
    .limit(1);
  if (error) return null;
  if ((data ?? []).length > 0) return null;
  return {
    id: 'daily-check-missing',
    severity: 'amber',
    title: 'Daily check not done today',
    body: 'Run the daily walk-around so the audit trail stays clean.',
    actionLabel: 'Start daily check',
    href: `/sites/${siteId}/checks/daily/run`,
  };
}

// ============================================================
// #9 Weekly inspection overdue (any week without a weekly run)
// ============================================================

async function getWeeklyCheckAlert(siteId: string): Promise<DashboardAlert | null> {
  const now = new Date();
  // Only fire on Friday afternoon onward, or any time after Friday
  const dow = now.getDay(); // 0 Sun, 5 Fri, 6 Sat
  if (dow > 0 && dow < 5) return null;

  // Look for a weekly run in the last 7 days
  const sevenDaysAgo = new Date(now.getTime() - 7 * DAY_MS).toISOString();
  const { data, error } = await supabase
    .from('tick_list_runs')
    .select('id')
    .eq('site_id', siteId)
    .eq('kind', 'weekly')
    .gte('created_at', sevenDaysAgo)
    .limit(1);
  if (error) return null;
  if ((data ?? []).length > 0) return null;
  return {
    id: 'weekly-check-overdue',
    severity: 'amber',
    title: 'Weekly inspection overdue',
    body: 'No weekly check logged in the last 7 days.',
    actionLabel: 'Start weekly',
    href: `/sites/${siteId}/checks/weekly/run`,
  };
}

// ============================================================
// Friday afternoon wrap-up nudge
// ============================================================
async function getWrapUpNudge(siteId: string): Promise<DashboardAlert | null> {
  const now = new Date();
  // Fire from Friday 14:00 to Sunday 23:59 only
  const dow = now.getDay();
  if (dow === 0) return null; // Sunday handled below — actually no, Sunday is 0 → fire
  // Match: Fri (5) afternoon, Sat (6), Sun (0)
  if (dow >= 1 && dow < 5) return null; // Mon-Thu: skip
  if (dow === 5 && now.getHours() < 14) return null; // Friday before 14:00: skip
  return {
    id: 'wrap-up',
    severity: 'amber',
    title: 'Weekly review',
    body: 'Roll-up of this week\'s checks, incidents, permits and next week\'s expiries — 5-minute review.',
    actionLabel: 'Open review',
    href: `/sites/${siteId}/wrap-up`,
  };
}

// ============================================================
// #3, #8 Org-wide expiries — insurance, training cards, plant certs
// ============================================================

async function getOrgWideAlerts(orgId: string): Promise<DashboardAlert[]> {
  const out: DashboardAlert[] = [];
  const now = Date.now();
  const in30 = now + 30 * DAY_MS;

  // Training records — use expires_on
  const { data: training } = await supabase
    .from('training_records')
    .select('expires_on, person_id')
    .not('expires_on', 'is', null);

  let trExpired = 0;
  let trExpiring = 0;
  for (const t of training ?? []) {
    if (!t.expires_on) continue;
    const exp = new Date(t.expires_on).getTime();
    if (exp < now) trExpired += 1;
    else if (exp < in30) trExpiring += 1;
  }

  // Subcontractors — pl_expires, el_expires
  const { data: subs } = await supabase
    .from('subcontractors')
    .select('pl_expires, el_expires');
  let insExpired = 0;
  let insExpiring = 0;
  for (const s of subs ?? []) {
    for (const exp of [s.pl_expires, s.el_expires]) {
      if (!exp) continue;
      const t = new Date(exp).getTime();
      if (t < now) insExpired += 1;
      else if (t < in30) insExpiring += 1;
    }
  }

  // Documents — expires_at where current and not marked 'not required'
  const { data: docs } = await supabase
    .from('documents')
    .select('expires_at')
    .eq('org_id', orgId)
    .eq('is_current', true)
    .neq('status', 'not_applicable')
    .not('expires_at', 'is', null);
  let docExpired = 0;
  let docExpiring = 0;
  for (const d of docs ?? []) {
    if (!d.expires_at) continue;
    const t = new Date(d.expires_at).getTime();
    if (t < now) docExpired += 1;
    else if (t < in30) docExpiring += 1;
  }

  const totalExpired = trExpired + insExpired + docExpired;
  const totalExpiring = trExpiring + insExpiring + docExpiring;

  if (totalExpired > 0) {
    out.push({
      id: 'expired',
      severity: 'red',
      title: `${totalExpired} cert${totalExpired === 1 ? '' : 's'} expired`,
      body: 'Training, insurance or compliance docs need renewing now.',
      actionLabel: 'View expiries',
      href: '/registers/expiries',
    });
  }
  if (totalExpiring > 0) {
    out.push({
      id: 'expiring',
      severity: 'amber',
      title: `${totalExpiring} cert${totalExpiring === 1 ? '' : 's'} expires within 30 days`,
      body: 'Plan the renewal now to avoid a red on the dashboard.',
      actionLabel: 'View expiries',
      href: '/registers/expiries',
    });
  }

  return out;
}

// Draft-incident alert removed — the two-stage "quick log → full report"
// flow was retired in favour of the single proper accident form. Any
// historical draft rows are left in place but no longer surfaced as a
// dashboard nudge.

// ============================================================
// First aider cover — RED if anyone's on site but no first aider is
// ============================================================
//
// HSE first-aid regulations (Health and Safety (First-Aid) Regs 1981,
// reinforced by ACOP L74) require employers to ensure adequate first
// aid cover during working hours. On a construction site with active
// sign-ins, "adequate" practically means at least one trained first
// aider (or Appointed Person on low-risk jobs) present.
//
// We can't enforce that, but we can warn loudly the moment someone signs
// in without a first aider already on site. Hard red — this is the kind
// of thing that becomes a corporate manslaughter issue if it goes wrong.

async function getFirstAiderAlert(siteId: string): Promise<DashboardAlert | null> {
  const { data, error } = await supabase
    .from('sign_ins')
    .select('id, is_first_aider')
    .eq('site_id', siteId)
    .is('time_out', null);
  if (error) return null;
  const onSite = data ?? [];
  if (onSite.length === 0) return null;          // Nobody on site — nothing to warn about.
  const firstAiders = onSite.filter((s) => s.is_first_aider).length;
  if (firstAiders > 0) return null;              // At least one first aider present — all good.

  return {
    id: 'no-first-aider-on-site',
    severity: 'red',
    title: 'No first aider signed in on this site',
    body:
      `${onSite.length} ${onSite.length === 1 ? 'person is' : 'people are'} signed in but no-one has ticked "first aider" today. ` +
      'HSE first-aid regulations require adequate first-aid cover during working hours.',
    actionLabel: 'Open Sign In / Out',
    href: `/sites/${siteId}/sign-in`,
  };
}

// Notes:
// #4 (accident not investigated) and #11 (subbie without RAMS) are deferred
// until the Open Issues aggregator + RAMS-by-company lookup are built.
// Adding them later requires only an additional helper in this module.
